import useTranslation from "@/hooks/use-translation";
import { Content } from "@/models/document";
import { Card, CardBody, Chip, Typography } from "@/components/ui";
import { FaClock, FaGlobe, FaShare, FaCheck, FaSpinner } from "react-icons/fa";
import React from "react";
import { safeString } from "@/utils/safe-value";

interface ListViewProps {
  items: Content[];
  displayMode?: string;
  /** When set, displays enrichment results from this field_out on each document */
  enrichmentFieldOut?: string;
}

type EnrichmentResult =
  | { kind: "list"; items: any[] }
  | { kind: "kv"; entries: { key: string; value: string }[] }
  | null;

/**
 * Resolve enrichment data from a document given the field_out path.
 * After unprefix the ES key "obj$extra.sentiment" becomes the flat key "extra.sentiment".
 * The value is doubly nested: { "extra.sentiment": { sentiment: "positive" } }.
 */
function getEnrichmentResult(item: any, fieldOut?: string): EnrichmentResult {
  if (!fieldOut) return null;
  const raw = item[fieldOut];
  if (!raw) return null;
  const inner = raw[fieldOut] || raw;

  // Array result (concepts, entities, keywords…)
  if (Array.isArray(inner)) return { kind: "list", items: inner };
  if (typeof inner === "object" && inner !== null) {
    for (const val of Object.values(inner)) {
      if (Array.isArray(val)) return { kind: "list", items: val as any[] };
    }
    // Scalar/KV result (sentiment, language…) — collect non-null leaf values
    const entries: { key: string; value: string }[] = [];
    for (const [k, v] of Object.entries(inner)) {
      if (v != null && typeof v !== "object") {
        entries.push({ key: k, value: String(v) });
      }
    }
    if (entries.length > 0) return { kind: "kv", entries };
  }
  if (typeof inner === "string") {
    return { kind: "kv", entries: [{ key: fieldOut.split(".").pop() || fieldOut, value: inner }] };
  }
  return null;
}

const CONCEPT_TYPE_COLORS: Record<string, string> = {
  Person: "bg-blue-500/15 text-blue-700 dark:text-blue-300",
  Organization: "bg-purple-500/15 text-purple-700 dark:text-purple-300",
  Location: "bg-green-500/15 text-green-700 dark:text-green-300",
  Event: "bg-orange-500/15 text-orange-700 dark:text-orange-300",
  Topic: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
};

const SENTIMENT_COLORS: Record<string, string> = {
  positive: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  negative: "bg-red-500/15 text-red-700 dark:text-red-300",
  neutral: "bg-slate-500/15 text-slate-600 dark:text-slate-300",
  mixed: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
};

const ListView: React.FC<ListViewProps> = ({ items, enrichmentFieldOut }) => {
  const { t } = useTranslation();

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getAuthorInfo = (item: any) => {
    const rawData = item.normalized ? item.normalized : item;
    return {
      username: safeString(rawData.author?.username || rawData.author?.title) || "Unknown",
      avatar: safeString(rawData.author?.media?.[0]?.url || rawData.author?.avatar),
      verified: rawData.author?.verified,
    };
  };

  const getContentInfo = (item: any) => {
    const rawData = item.normalized ? item.normalized : item;
    const title = safeString(rawData.title);
    const text = safeString(rawData.text || rawData.description);
    return {
      title: title || (text ? text.substring(0, 100) + "..." : ""),
      text: text,
      date: safeString(rawData.date || rawData.published_time),
      language: safeString(rawData.language || rawData.lang || item.extra?.lang),
      url: safeString(rawData.url),
      stats: rawData.stats,
    };
  };

  return (
    <div className="space-y-3">
      {items.map((item, index) => {
        const authorInfo = getAuthorInfo(item);
        const contentInfo = getContentInfo(item);
        const enrichmentStatus = (item as any)._enrichment_status;
        const enrichmentResult = getEnrichmentResult(item, enrichmentFieldOut);

        return (
          <Card
            key={item.id || index}
            className="cursor-pointer transition-shadow duration-200 hover:shadow-arc-md"
            onClick={() => contentInfo.url && window.open(contentInfo.url, "_blank")}
          >
            <CardBody className="p-4">
              <div className="flex items-start space-x-4">
                {/* Avatar */}
                <div className="size-12 shrink-0 rounded-full overflow-hidden bg-surface-secondary">
                  {authorInfo.avatar ? (
                    <img
                      src={authorInfo.avatar}
                      alt={authorInfo.username}
                      className="size-full object-cover"
                    />
                  ) : (
                    <div className="size-full flex items-center justify-center text-content-tertiary text-lg font-semibold">
                      {authorInfo.username.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>

                {/* Contenu principal */}
                <div className="min-w-0 flex-1">
                  {/* Header avec auteur et date */}
                  <div className="mb-2 flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Typography variant="text-sm" className="font-semibold text-content-primary">
                        {authorInfo.username}
                      </Typography>
                      {authorInfo.verified && (
                        <Chip size="sm" variant="soft" color="info">✓</Chip>
                      )}
                      {enrichmentStatus && (
                        <Chip
                          size="sm"
                          variant="soft"
                          color={enrichmentStatus === "completed" ? "success" : "warning"}
                        >
                          {enrichmentStatus === "completed" ? (
                            <span className="flex items-center gap-1"><FaCheck className="text-[9px]" /> Enrichi</span>
                          ) : (
                            <span className="flex items-center gap-1"><FaSpinner className="text-[9px] animate-spin" /> En cours</span>
                          )}
                        </Chip>
                      )}
                    </div>
                    <div className="flex items-center space-x-2 text-content-tertiary">
                      <FaClock className="size-4" />
                      <Typography variant="text-xs">
                        {contentInfo.date && formatDate(contentInfo.date)}
                      </Typography>
                    </div>
                  </div>

                  {/* Titre et texte */}
                  <div className="mb-3">
                    {contentInfo.title && (
                      <Typography
                        variant="h6"
                        className="mb-2 line-clamp-1 font-semibold"
                      >
                        {contentInfo.title}
                      </Typography>
                    )}
                    <Typography
                      variant="text-sm"
                      className="line-clamp-2"
                      color="secondary"
                    >
                      {contentInfo.text}
                    </Typography>
                  </div>

                  {/* Enrichment results */}
                  {enrichmentResult?.kind === "list" && enrichmentResult.items.length > 0 && (
                    <div className="mb-3 flex flex-wrap gap-1.5">
                      {enrichmentResult.items.slice(0, 6).map((concept: any, i: number) => {
                        const label = concept.title || concept.name || concept.text || String(concept);
                        const type = concept.type || "";
                        const colorClass = CONCEPT_TYPE_COLORS[type] || "bg-surface-secondary text-content-secondary";
                        return (
                          <span
                            key={i}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${colorClass}`}
                            title={concept.description || type}
                          >
                            {label}
                            {type && (
                              <span className="opacity-60 text-[10px]">{type}</span>
                            )}
                          </span>
                        );
                      })}
                      {enrichmentResult.items.length > 6 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs text-content-tertiary bg-surface-secondary">
                          +{enrichmentResult.items.length - 6}
                        </span>
                      )}
                    </div>
                  )}
                  {enrichmentResult?.kind === "kv" && (
                    <div className="mb-3 flex flex-wrap gap-1.5">
                      {enrichmentResult.entries.map(({ key, value }) => {
                        const colorClass = SENTIMENT_COLORS[value.toLowerCase()] || "bg-surface-secondary text-content-secondary";
                        return (
                          <span
                            key={key}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${colorClass}`}
                          >
                            <span className="capitalize">{key}</span>
                            <span className="opacity-70">·</span>
                            <span className="capitalize">{value}</span>
                          </span>
                        );
                      })}
                    </div>
                  )}

                  {/* Footer avec métadonnées et stats */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      {contentInfo.language && (
                        <div className="flex items-center space-x-1">
                          <FaGlobe className="size-4 text-content-tertiary" />
                          <Typography variant="text-xs" color="tertiary">
                            {contentInfo.language.toUpperCase()}
                          </Typography>
                        </div>
                      )}
                      {contentInfo.url && (
                        <div className="flex items-center space-x-1">
                          <FaShare className="size-4 text-content-tertiary" />
                          <Typography variant="text-xs" color="tertiary">
                            {t("external_link")}
                          </Typography>
                        </div>
                      )}
                    </div>

                    {/* Statistiques */}
                    {contentInfo.stats && (
                      <div className="flex items-center space-x-3">
                        {Object.entries(contentInfo.stats).slice(0, 3).map(([key, value]) => {
                          if (value === undefined || value === null) return null;
                          return (
                            <div key={key} className="flex items-center space-x-1">
                              <Typography variant="text-xs" className="font-medium text-content-secondary">
                                {typeof value === "number" ? value.toLocaleString() : String(value)}
                              </Typography>
                              <Typography variant="text-xs" className="capitalize text-content-tertiary">
                                {key}
                              </Typography>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        );
      })}
    </div>
  );
};

export default ListView;
